import type { EntityPluginContext } from "@brains/plugins";
import type { Logger } from "@brains/utils/logger";
import {
  BaseJobHandler,
  failPendingEntity,
  findEntityByIdentifier,
  saveProcessedEntity,
} from "@brains/plugins";
import type { ProgressReporter } from "@brains/utils/progress";
import { imageAdapter, setCoverImageId } from "@brains/image";
import { fetchStyleGuide, formatVisualGuidance } from "@brains/contracts";
import { getErrorMessage } from "@brains/utils/error";
import { slugify } from "@brains/utils/string-utils";
import { z } from "@brains/utils/zod";
import { PROGRESS_STEPS, JobResult } from "@brains/contracts";
import { buildImageBasePrompt } from "../lib/build-image-base-prompt";
import { getDistillableEntityContent } from "../lib/distillable-content";

/**
 * Schema for AI-distilled image prompt
 */
const imagePromptSchema = z.object({
  imagePrompt: z
    .string()
    .describe(
      "A concise, vivid image prompt capturing the core visual concept",
    ),
});

/**
 * Schema for image generation job data
 */
export type ImageAspectRatio = "1:1" | "16:9" | "9:16" | "4:3" | "3:4";

export interface ImageGenerationJobData {
  prompt: string;
  title?: string | undefined;
  aspectRatio?: ImageAspectRatio | undefined;
  targetEntityType?: string | undefined;
  targetEntityId?: string | undefined;
  entityTitle?: string | undefined;
  entityContent?: string | undefined;
}

export const imageGenerationJobDataSchema: z.ZodType<ImageGenerationJobData> =
  z.object({
    /** Text prompt for image generation (used directly when provided without entityContent) */
    prompt: z.string(),
    /** Title for the generated image (derived from prompt if omitted) */
    title: z.string().optional(),
    /** Aspect ratio for the generated image */
    aspectRatio: z.enum(["1:1", "16:9", "9:16", "4:3", "3:4"]).optional(),
    /** Target entity type to update with coverImageId (optional) */
    targetEntityType: z.string().optional(),
    /** Target entity ID to update with coverImageId (required if targetEntityType is set) */
    targetEntityId: z.string().optional(),
    /** Entity title for AI prompt distillation (optional) */
    entityTitle: z.string().optional(),
    /** Entity content for AI prompt distillation (optional, triggers AI prompt generation) */
    entityContent: z.string().optional(),
  });

interface ImageGenerationResult {
  success: boolean;
  imageId?: string;
  error?: string;
}

/**
 * Job handler for AI image generation
 *
 * This runs asynchronously so tool calls aren't blocked by image API latency.
 * The handler:
 * 1. Checks if image generation is available
 * 2. Generates image via AI service
 * 3. Creates image entity from generated data
 * 4. Optionally updates target entity's coverImageId
 */
export class ImageGenerationJobHandler extends BaseJobHandler<
  "image-generate",
  ImageGenerationJobData,
  ImageGenerationResult
> {
  private readonly context: EntityPluginContext;

  constructor(context: EntityPluginContext, logger: Logger) {
    super(logger, {
      schema: imageGenerationJobDataSchema,
      jobTypeName: "image-generate",
    });
    this.context = context;
  }

  async process(
    data: ImageGenerationJobData,
    jobId: string,
    progressReporter: ProgressReporter,
  ): Promise<ImageGenerationResult> {
    const { prompt, aspectRatio, targetEntityType, targetEntityId } = data;
    const title =
      data.title ??
      (targetEntityId ? `cover-${targetEntityId}` : prompt.slice(0, 60).trim());

    this.logger.debug("Starting image generation job", {
      jobId,
      title,
      hasTarget: !!targetEntityType,
    });

    const imageId = slugify(title);

    try {
      await this.reportProgress(progressReporter, {
        progress: PROGRESS_STEPS.INIT,
        message: "Checking image generation availability",
      });

      // Step 1: Check if image generation is available
      if (!this.context.ai.canGenerateImages()) {
        const error = new Error(
          "Image generation not available: no API key configured",
        );
        await failPendingEntity({
          entityService: this.context.entityService,
          entityType: "image",
          id: imageId,
          error: error.message,
        });
        return JobResult.failure(error);
      }

      const styleGuide = await fetchStyleGuide(this.context.entityService);
      const visualGuidance = formatVisualGuidance(styleGuide).trim();

      // Step 1.5: Distill prompt using AI when entity content is provided
      let finalPrompt = prompt;
      const entityContent = getDistillableEntityContent(data.entityContent);
      if (entityContent) {
        await this.reportProgress(progressReporter, {
          progress: PROGRESS_STEPS.FETCH,
          message: "Distilling image prompt from content",
        });

        try {
          const { object } = await this.context.ai.generateObject(
            `You are a visual concept designer. Given source content, describe WHAT TO DEPICT, not how to render it.

Rules:
- Identify one clear visual subject, scene, or metaphor that communicates the central idea
- Avoid generic visual clichés unless they are genuinely central to the source
- Describe only subjects and their spatial relationships; omit colors, lighting, materials, medium, and rendering instructions
- Keep the result to 1-2 sentences
${visualGuidance ? `- Let the supplied visual guidance influence concept selection without repeating rendering instructions\n\nVisual guidance:\n${visualGuidance}\n` : ""}
Title: "${data.entityTitle ?? title}"

Content:
${entityContent}`,
            imagePromptSchema,
          );
          finalPrompt = `${prompt.trim()} ${object.imagePrompt}`;
        } catch (error) {
          this.logger.warn("AI prompt distillation failed, using fallback", {
            error: getErrorMessage(error),
          });
          // Fall back to using the raw prompt
        }
      }

      await this.reportProgress(progressReporter, {
        progress: PROGRESS_STEPS.PROCESS,
        message: "Generating image",
      });

      // Step 2: Apply style-guide visual direction
      const basePrompt = buildImageBasePrompt(styleGuide);
      const styledPrompt = basePrompt + finalPrompt;

      // Step 3: Generate image
      let generationResult;
      try {
        generationResult = await this.context.ai.generateImage(styledPrompt, {
          ...(aspectRatio && { aspectRatio }),
        });
      } catch (error) {
        this.logger.error("Image generation failed", {
          jobId,
          error: getErrorMessage(error),
        });
        return JobResult.failure(error);
      }

      await this.reportProgress(progressReporter, {
        progress: PROGRESS_STEPS.GENERATE,
        message: "Creating image entity",
      });

      // Step 3: Create or update image entity
      const entityData = imageAdapter.createImageEntity({
        dataUrl: generationResult.dataUrl,
        title,
        status: "draft",
        attachmentType: "generated",
        ...(targetEntityType && { sourceEntityType: targetEntityType }),
        ...(targetEntityId && { sourceEntityId: targetEntityId }),
      });

      await saveProcessedEntity({
        entityService: this.context.entityService,
        entity: {
          ...entityData,
          id: imageId,
        },
      });

      this.logger.debug("Created image entity", { imageId });

      // Step 4: Optionally update target entity
      if (targetEntityType && targetEntityId) {
        await this.reportProgress(progressReporter, {
          progress: PROGRESS_STEPS.SAVE,
          message: `Updating ${targetEntityType} with cover image`,
        });

        const targetEntity = await findEntityByIdentifier(
          this.context.entityService,
          targetEntityType,
          targetEntityId,
          this.logger,
        );

        if (!targetEntity) {
          return JobResult.failure(
            new Error(
              `Target entity not found: ${targetEntityType}/${targetEntityId}`,
            ),
          );
        }

        const updated = setCoverImageId(targetEntity, imageId);
        await this.context.entities.update(updated);

        this.logger.debug("Updated target entity with cover image", {
          targetEntityType,
          targetEntityId,
          imageId,
        });
      }

      await this.reportProgress(progressReporter, {
        progress: PROGRESS_STEPS.COMPLETE,
        message: "Image generation complete",
      });

      this.logger.info("Image generation job complete", {
        jobId,
        imageId,
        targetEntityType,
        targetEntityId,
      });

      return { success: true, imageId };
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      this.logger.error("Image generation job failed", {
        jobId,
        error: errorMessage,
      });
      await failPendingEntity({
        entityService: this.context.entityService,
        entityType: "image",
        id: imageId,
        error: errorMessage,
      });
      return JobResult.failure(error);
    }
  }

  protected override summarizeDataForLog(
    data: ImageGenerationJobData,
  ): Record<string, unknown> {
    return {
      title: data.title,
      promptLength: data.prompt.length,
      aspectRatio: data.aspectRatio,
      targetEntityType: data.targetEntityType,
      targetEntityId: data.targetEntityId,
    };
  }
}
